import { useMemo } from 'react'
import { FieldValues, UseFormReturn, useWatch } from 'react-hook-form'

import { useDebouncedValue } from '@hooks/useDebounce'
import {
  BACKEND_LIMIT,
  formatBytes,
  getProjectedUrlLength,
  getUrlLengthStatus,
  type UrlLengthStatus,
} from '@lib/urlLength'

export interface UrlLengthCheckResult {
  status: UrlLengthStatus
  currentLength: number
  percentageUsed: number
  formattedCurrent: string
  formattedLimit: string
}

const URL_LENGTH_DEBOUNCE_MS = 300

const OK_RESULT: UrlLengthCheckResult = {
  status: 'ok',
  currentLength: 0,
  percentageUsed: 0,
  formattedCurrent: '0B',
  formattedLimit: formatBytes(BACKEND_LIMIT),
}

export function useUrlLengthCheck<T extends FieldValues>(
  form: UseFormReturn<T>,
  pathname: string | undefined,
): UrlLengthCheckResult {
  const formValues = useWatch({ control: form.control })

  // Serialize to stabilize reference — useWatch returns a new object on every render,
  // which would reset the debounce timer without this.
  const serialized = JSON.stringify(formValues)
  const debouncedSerialized = useDebouncedValue(serialized, URL_LENGTH_DEBOUNCE_MS)

  return useMemo(() => {
    if (pathname === undefined) return OK_RESULT

    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    const values = JSON.parse(debouncedSerialized)
    const length = getProjectedUrlLength(origin, pathname, values)

    return {
      status: getUrlLengthStatus(length),
      currentLength: length,
      percentageUsed: Math.min(Math.round((length / BACKEND_LIMIT) * 100), 100),
      formattedCurrent: formatBytes(length),
      formattedLimit: formatBytes(BACKEND_LIMIT),
    }
  }, [pathname, debouncedSerialized])
}
